import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  DropdownMenu,
  Flex,
  IconButton,
  Tooltip,
} from "@radix-ui/themes";
import {
  Plus,
  Pencil,
  Check,
  X,
  Shield,
  Share2,
  Star,
  MoreHorizontal,
} from "lucide-react";
import {
  GridLayout,
  Layout,
  LayoutItem as GridLayoutItem,
} from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { SkeletonDashboard } from "@/components/ui/Skeleton";
import { useElementSize } from "@/hooks/useElementSize";
import PageTemplate from "@/components/page-template/PageTemplate";
import { useAuth } from "@/providers/AuthProvider";
import { dashboardService } from "@/services/breeze/DashboardService";
import {
  CreateDashboardWidgetRequest,
  Dashboard as DashboardType,
  DashboardWidget,
  LayoutItem,
  WidgetDataMap,
} from "@/types/breeze/Dashboard";
import { WidgetRenderer } from "@/components/dashboard/WidgetRenderer";
import { AddWidgetModal } from "@/components/dashboard/AddWidgetModal";
import ChartBuilderModal from "@/components/dashboard/ChartBuilderModal";
import MaterialisedViewBuilderModal from "@/components/dashboard/MaterialisedViewBuilderModal";
import { DashboardSwitcher } from "@/components/dashboard/DashboardSwitcher";
import { NewDashboardModal } from "@/components/dashboard/NewDashboardModal";
import { remapLayoutWidgetIds } from "./dashboardLayout";
import {
  getCachedSkeletonLayout,
  saveSkeletonLayout,
} from "./dashboardLayoutCache";
import { toast } from "react-hot-toast";
import { useEntities } from "@/hooks/useCommonQueries";
import { useQuery } from "@tanstack/react-query";
import { workflowService } from "@/services/breeze/WorkflowService";
import { OnboardingChecklist } from "@/components/dashboard/OnboardingChecklist";

const GRID_COLS = 12;
const ROW_HEIGHT = 90;
const DEFAULT_WIDGET_SIZE = { w: 4, h: 3 };
const DEFAULT_WIDGET_SIZE_BY_TYPE: Record<string, { w: number; h: number }> = {
  quick_action: { w: 3, h: 1 },
  getting_started: { w: 6, h: 2 },
};

function widgetMinH(widget: DashboardWidget): number {
  const cfg = safeParseConfig(widget.config_json);
  if (widget.type === "quick_action") {
    return cfg?.description ? 2 : 1;
  }
  if (widget.type === "getting_started") {
    const body = typeof cfg?.body === "string" ? cfg.body : "";
    const actions = Array.isArray(cfg?.actions) ? cfg.actions.length : 2;
    let h = 2;
    if (body.length > 160) h = 3;
    if (actions > 2) h += 1;
    return h;
  }
  return 1;
}

function safeParseConfig(json: string | null): Record<string, unknown> | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export default function Dashboard() {
  const { currentUser, isTenantAdmin, currentTenant } = useAuth();
  const { dashboardId: routeDashboardId } = useParams<{
    dashboardId?: string;
  }>();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardType | null>(null);
  const [dashboards, setDashboards] = useState<DashboardType[]>([]);
  const [data, setData] = useState<WidgetDataMap>({});
  const [loading, setLoading] = useState(true);

  // Onboarding Checklist Hooks & State
  const { data: entities } = useEntities();
  const { data: workflows } = useQuery({
    queryKey: ["workflows"],
    queryFn: () => workflowService.getAllWorkflows(),
    enabled: !!currentUser,
  });
  const [dismissedOnboarding, setDismissedOnboarding] = useState(false);

  useEffect(() => {
    if (currentTenant) {
      setDismissedOnboarding(localStorage.getItem(`aod_${currentTenant.id}`) === "true");
    } else {
      setDismissedOnboarding(false);
    }
  }, [currentTenant]);

  const handleDismissOnboarding = () => {
    if (currentTenant) {
      localStorage.setItem(`aod_${currentTenant.id}`, "true");
      setDismissedOnboarding(true);
    }
  };

  const allStepsCompleted =
    entities &&
    entities.length > 0 &&
    workflows &&
    workflows.length > 0 &&
    localStorage.getItem("anythink_visited_docs") === "true";

  const showOnboarding =
    entities && workflows && !allStepsCompleted && !dismissedOnboarding;
  const [editing, setEditing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DashboardWidget | null>(null);
  const [newDashboardOpen, setNewDashboardOpen] = useState(false);
  // Tells the load effect to skip re-fetching when handleCreateDashboard already loaded that id locally.
  const skipNextLoadRef = useRef<number | null>(null);
  // Observe the wrapper Box so the grid reflows whenever the content area
  // changes — sidebar collapse, AI panel open/drag-resize, window resize.
  const {
    size: containerSize,
    ref: containerRef,
    measure: measureWidth,
  } = useElementSize<HTMLDivElement>();
  const containerWidth = containerSize.width;

  // Refresh the switcher list alongside whatever dashboard we're viewing.
  const refreshDashboardList = useCallback(async () => {
    try {
      const all = await dashboardService.list();
      setDashboards(all);
    } catch {
      // Non-fatal — switcher just stays with whatever it had.
    }
  }, []);

  // Load the effective dashboard: route-specified if present, otherwise the user's home.
  useEffect(() => {
    if (
      routeDashboardId &&
      skipNextLoadRef.current === Number(routeDashboardId)
    ) {
      skipNextLoadRef.current = null;
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const d = routeDashboardId
          ? await dashboardService.getById(Number(routeDashboardId))
          : await dashboardService.getMine();
        if (cancelled) return;
        setDashboard(d);
        saveSkeletonLayout(
          routeDashboardId ? Number(routeDashboardId) : "home",
          d.layout_json,
        );
        const widgetData = await dashboardService.getData(d.id);
        if (cancelled) return;
        setData(widgetData);
        await refreshDashboardList();
      } catch (err) {
        toast.error("Failed to load dashboard");
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [routeDashboardId, refreshDashboardList]);

  const widgets = useMemo(() => dashboard?.widgets ?? [], [dashboard]);

  const layout: GridLayoutItem[] = useMemo(() => {
    if (!dashboard) return [];
    const saved = parseLayout(dashboard.layout_json);
    return widgets.map((w, i) => {
      const minH = widgetMinH(w);
      const existing = saved.find((l) => l.widgetId === w.id);
      if (existing) {
        return {
          i: String(w.id),
          x: existing.x,
          y: existing.y,
          w: existing.w,
          h: Math.max(existing.h, minH),
          minH,
        };
      }
      // Auto-place missing widgets in a simple flow
      const size = DEFAULT_WIDGET_SIZE_BY_TYPE[w.type] ?? DEFAULT_WIDGET_SIZE;
      return {
        i: String(w.id),
        x: (i * size.w) % GRID_COLS,
        y: Math.floor((i * size.w) / GRID_COLS) * size.h,
        w: size.w,
        h: Math.max(size.h, minH),
        minH,
      };
    });
  }, [dashboard, widgets]);

  const handleLayoutChange = useCallback(
    (newLayout: Layout) => {
      if (!dashboard || !editing) return;
      const items: LayoutItem[] = newLayout.map((l) => ({
        widgetId: Number(l.i),
        x: l.x,
        y: l.y,
        w: l.w,
        h: l.h,
      }));
      setDashboard({ ...dashboard, layout_json: JSON.stringify(items) });
    },
    [dashboard, editing],
  );

  const handleRemoveWidget = useCallback(
    (widgetId: number) => {
      if (!dashboard) return;
      setDashboard({
        ...dashboard,
        widgets: widgets.filter((w) => w.id !== widgetId),
      });
    },
    [dashboard, widgets],
  );

  const handleEditWidget = useCallback((widget: DashboardWidget) => {
    setEditTarget(widget);
  }, []);

  const handleWidgetSubmit = useCallback(
    async (submitted: CreateDashboardWidgetRequest) => {
      if (!dashboard) return;

      // Keep in sync with the server's PreviewWidget resolvers.
      const needsPreview = ["list", "chart", "materialised_view"].includes(submitted.type);
      const previewPromise = needsPreview
        ? dashboardService
            .previewWidget(submitted.type, submitted.config_json ?? null)
            .catch(() => undefined)
        : Promise.resolve(undefined);

      if (editTarget) {
        setDashboard({
          ...dashboard,
          widgets: widgets.map((w) =>
            w.id === editTarget.id
              ? {
                  ...w,
                  type: submitted.type,
                  title: submitted.title,
                  config_json: submitted.config_json ?? null,
                }
              : w,
          ),
        });
        const preview = await previewPromise;
        if (preview !== undefined) {
          setData((prev) => ({ ...prev, [editTarget.id]: preview }));
        }
        setEditTarget(null);
        return;
      }

      const tempId = -Date.now();
      setDashboard({
        ...dashboard,
        widgets: [
          ...widgets,
          {
            id: tempId,
            type: submitted.type,
            title: submitted.title,
            config_json: submitted.config_json ?? null,
            sort_order: widgets.length,
          },
        ],
      });
      const preview = await previewPromise;
      if (preview !== undefined) {
        setData((prev) => ({ ...prev, [tempId]: preview }));
      }
    },
    [dashboard, widgets, editTarget],
  );

  const handleSave = useCallback(async () => {
    if (!dashboard) return;
    try {
      // Temp ids from handleWidgetSubmit are negative and local-only — drop them so the server inserts.
      const orderedWidgets = widgets.map((w, i) => ({
        id: w.id > 0 ? w.id : undefined,
        type: w.type,
        title: w.title,
        config_json: w.config_json,
        sort_order: i,
      }));
      const saved = await dashboardService.update(dashboard.id, {
        layout_json: dashboard.layout_json ?? undefined,
        widgets: orderedWidgets,
      });

      // Rewrite temp ids in layout_json to the server-minted ones, or new widgets lose their positions.
      const savedWidgets = [...saved.widgets].sort(
        (a, b) => a.sort_order - b.sort_order,
      );
      const remap = remapLayoutWidgetIds(
        widgets,
        savedWidgets,
        saved.layout_json,
      );
      if (remap.changed && remap.json !== null) {
        await dashboardService.update(saved.id, { layout_json: remap.json });
      }

      setDashboard({ ...saved, layout_json: remap.json });
      saveSkeletonLayout(saved.id, remap.json);
      const widgetData = await dashboardService.getData(saved.id);
      setData(widgetData);
      setEditing(false);
      toast.success("Dashboard saved");
    } catch (err) {
      toast.error("Failed to save dashboard");
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }, [dashboard, widgets]);

  const handleCancel = useCallback(async () => {
    try {
      const d = await dashboardService.getMine();
      setDashboard(d);
      setEditing(false);
    } catch {
      toast.error("Failed to reload dashboard");
    }
  }, []);

  const handleSelectDashboard = useCallback(
    (id: number) => {
      if (!currentTenant || id === dashboard?.id) return;
      navigate(`/org/${currentTenant.id}/dashboard/${id}`);
    },
    [currentTenant, dashboard?.id, navigate],
  );

  const handleCreateDashboard = useCallback(
    async (values: {
      name: string;
      description?: string;
      isShared: boolean;
    }) => {
      if (!currentTenant) return;
      try {
        const created = await dashboardService.create({
          name: values.name,
          description: values.description,
          is_default: false,
          is_shared: values.isShared,
          is_home: false,
          widgets: [],
        });
        toast.success(`Created "${created.name}"`);

        // Load locally and replace-navigate — a route-driven reload here races with the modal's Radix Dialog close and leaves body { pointer-events: none }.
        const widgetData = await dashboardService.getData(created.id);
        skipNextLoadRef.current = created.id;
        setDashboard(created);
        saveSkeletonLayout(created.id, created.layout_json);
        setData(widgetData);
        await refreshDashboardList();
        setEditing(true);
        navigate(`/org/${currentTenant.id}/dashboard/${created.id}`, {
          replace: true,
        });
      } catch (err) {
        toast.error("Failed to create dashboard");
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    [currentTenant, navigate, refreshDashboardList],
  );

  // Radix Dialog occasionally leaves body { pointer-events: none } when two dialogs close in the same tick — see CLAUDE.md.
  useEffect(() => {
    const anyOpen = addOpen || !!editTarget || newDashboardOpen;
    if (anyOpen) return;
    const t = setTimeout(() => {
      if (document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    }, 300);
    return () => clearTimeout(t);
  }, [addOpen, editTarget, newDashboardOpen]);

  const handleSetHome = useCallback(async () => {
    if (!dashboard) return;
    try {
      await dashboardService.setHome(dashboard.id);
      setDashboard({ ...dashboard, is_home: true });
      await refreshDashboardList();
      toast.success(`"${dashboard.name}" is now your home dashboard`);
    } catch {
      toast.error("Failed to set home");
    }
  }, [dashboard, refreshDashboardList]);

  const handleSetSharing = useCallback(async (mode: "private" | "view" | "edit") => {
    if (!dashboard || dashboard.is_default) return;
    try {
      const updated = await dashboardService.update(dashboard.id, {
        is_shared: mode !== "private",
        allow_shared_edit: mode === "edit",
      });
      setDashboard(updated);
      await refreshDashboardList();
      toast.success(
        mode === "private" ? "Dashboard is private"
          : mode === "view" ? "Shared, view only"
          : "Shared, editable by team",
      );
    } catch {
      toast.error("Failed to update sharing");
    }
  }, [dashboard, refreshDashboardList]);

  const handlePromoteToDefault = useCallback(async () => {
    if (!dashboard) return;
    if (!confirm("Apply this layout as the tenant default for new users?"))
      return;
    try {
      await dashboardService.promoteToDefault(dashboard.id);
      toast.success("Tenant default updated");
    } catch (err) {
      toast.error("Failed to promote");
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }, [dashboard]);

  // Re-measure once the dashboard loads (the Box only enters the DOM
  // after loading clears, and the hook's initial effect runs before that).
  useEffect(() => {
    if (!loading && dashboard) {
      requestAnimationFrame(measureWidth);
    }
  }, [loading, dashboard, measureWidth]);

  // Track pointer drags and re-measure on pointermove while held, plus on
  // pointerup. Covers AI-sidebar resize handles and any other manual resize
  // that the Box's own ResizeObserver may not pick up cleanly.
  useEffect(() => {
    let pressed = false;
    const onDown = () => {
      pressed = true;
    };
    const onMove = () => {
      if (pressed) requestAnimationFrame(measureWidth);
    };
    const onUp = () => {
      pressed = false;
      requestAnimationFrame(measureWidth);
    };
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [measureWidth]);

  if (!currentUser || loading || !dashboard) {
    const cachedLayout = getCachedSkeletonLayout(
      routeDashboardId ? Number(routeDashboardId) : "home",
    );
    return (
      <PageTemplate
        title={
          currentUser
            ? `👋 Hi ${currentUser.first_name || currentUser.email}`
            : ""
        }
        description="Loading dashboard…"
      >
        <SkeletonDashboard layout={cachedLayout} rowHeight={ROW_HEIGHT} />
      </PageTemplate>
    );
  }

  const canEditCurrent = dashboard.is_default
    ? isTenantAdmin
    : dashboard.owner_user_id === currentUser.id || (dashboard.is_shared && dashboard.allow_shared_edit);
  const canShareCurrent =
    !dashboard.is_default && dashboard.owner_user_id === currentUser.id;

  return (
    <PageTemplate
      title={`👋 Hi ${currentUser.first_name || currentUser.email}`}
      description={dashboard.description ?? "Your dashboard"}
      rightContent={
        // Layout: [switcher] ···gap··· [primary actions]. The bigger gap between
        // the two groups separates "what dashboard am I on" (context) from
        // "what can I do here" (actions).
        <Flex gap="5" align="center">
          <DashboardSwitcher
            dashboards={dashboards}
            currentId={dashboard.id}
            currentUserId={currentUser.id}
            onSelect={handleSelectDashboard}
            onCreateNew={() => setNewDashboardOpen(true)}
          />
          {editing ? (
            <Flex gap="2" align="center">
              <Button variant="soft" onClick={() => setAddOpen(true)}>
                <Plus size={14} /> Add widget
              </Button>
              {(isTenantAdmin || canShareCurrent) && (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger>
                    <IconButton
                      variant="soft"
                      size="2"
                      aria-label="More actions"
                    >
                      <MoreHorizontal size={14} />
                    </IconButton>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content>
                    {canShareCurrent && (
                      <>
                        <DropdownMenu.Label>Sharing</DropdownMenu.Label>
                        <DropdownMenu.RadioGroup
                          value={
                            !dashboard.is_shared
                              ? "private"
                              : dashboard.allow_shared_edit
                                ? "edit"
                                : "view"
                          }
                          onValueChange={(v) => handleSetSharing(v as "private" | "view" | "edit")}
                        >
                          <DropdownMenu.RadioItem value="private">
                            Private (only me)
                          </DropdownMenu.RadioItem>
                          <DropdownMenu.RadioItem value="view">
                            Shared — view only
                          </DropdownMenu.RadioItem>
                          <DropdownMenu.RadioItem value="edit">
                            Shared — anyone can edit
                          </DropdownMenu.RadioItem>
                        </DropdownMenu.RadioGroup>
                        {isTenantAdmin && <DropdownMenu.Separator />}
                      </>
                    )}
                    {isTenantAdmin && (
                      <DropdownMenu.Item onSelect={handlePromoteToDefault}>
                        <Shield size={14} /> Save as tenant default
                      </DropdownMenu.Item>
                    )}
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              )}
              <Button variant="soft" color="gray" onClick={handleCancel}>
                <X size={14} /> Cancel
              </Button>
              <Button onClick={handleSave} disabled={!canEditCurrent}>
                <Check size={14} /> Save
              </Button>
            </Flex>
          ) : (
            <Flex gap="2" align="center">
              {canShareCurrent && (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger>
                    <IconButton
                      size="2"
                      variant={dashboard.is_shared ? "solid" : "soft"}
                      aria-label="Sharing"
                    >
                      <Share2 size={14} />
                    </IconButton>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content>
                    <DropdownMenu.Label>Sharing</DropdownMenu.Label>
                    <DropdownMenu.CheckboxItem
                      checked={!dashboard.is_shared}
                      onSelect={() => handleSetSharing("private")}
                    >
                      Private (only me)
                    </DropdownMenu.CheckboxItem>
                    <DropdownMenu.CheckboxItem
                      checked={dashboard.is_shared && !dashboard.allow_shared_edit}
                      onSelect={() => handleSetSharing("view")}
                    >
                      Shared — view only
                    </DropdownMenu.CheckboxItem>
                    <DropdownMenu.CheckboxItem
                      checked={dashboard.is_shared && dashboard.allow_shared_edit}
                      onSelect={() => handleSetSharing("edit")}
                    >
                      Shared — anyone can edit
                    </DropdownMenu.CheckboxItem>
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              )}
              {canShareCurrent && !dashboard.is_home && (
                <Tooltip content="Set as home dashboard">
                  <IconButton
                    size="2"
                    variant="soft"
                    onClick={handleSetHome}
                    aria-label="Set as home"
                  >
                    <Star size={14} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip content="Customize">
                <IconButton
                  size="2"
                  variant="soft"
                  onClick={() => setEditing(true)}
                  disabled={!canEditCurrent}
                  aria-label="Customize dashboard"
                >
                  <Pencil size={14} />
                </IconButton>
              </Tooltip>
            </Flex>
          )}
        </Flex>
      }
    >
      {showOnboarding && (
        <OnboardingChecklist
          entityCount={entities.length}
          workflowCount={workflows?.length ?? 0}
          onDismiss={handleDismissOnboarding}
        />
      )}
      <Box
        // @ts-ignore
        ref={containerRef}
        style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}
      >
        <GridLayout
          className="layout"
          layout={layout}
          width={containerWidth}
          gridConfig={{
            cols: GRID_COLS,
            rowHeight: ROW_HEIGHT,
            margin: [12, 12],
            containerPadding: [0, 0],
          }}
          dragConfig={{ enabled: editing }}
          resizeConfig={{ enabled: editing }}
          onLayoutChange={handleLayoutChange}
        >
          {widgets.map((widget) => (
            <div key={String(widget.id)}>
              <WidgetRenderer
                widget={widget}
                data={data[widget.id]}
                loading={loading}
                editing={editing}
                onRemove={handleRemoveWidget}
                onEdit={handleEditWidget}
              />
            </div>
          ))}
        </GridLayout>
      </Box>

      {(() => {
        const chartEditId = editTarget?.type === "chart"
          ? Number(safeParseConfig(editTarget.config_json)?.chartId) || null
          : null;
        const materialisedViewEditId = editTarget?.type === "materialised_view"
          ? Number(safeParseConfig(editTarget.config_json)?.viewId) || null
          : null;
        const hasSpecialisedEditor = !!chartEditId || !!materialisedViewEditId;
        return (
          <>
            <AddWidgetModal
              open={addOpen || (!!editTarget && !hasSpecialisedEditor)}
              onOpenChange={(o) => {
                if (!o) {
                  setAddOpen(false);
                  if (!hasSpecialisedEditor) setEditTarget(null);
                } else if (!editTarget) {
                  setAddOpen(true);
                }
              }}
              onSubmit={handleWidgetSubmit}
              existing={hasSpecialisedEditor ? null : editTarget}
            />
            <ChartBuilderModal
              open={!!chartEditId}
              onOpenChange={(o) => { if (!o) setEditTarget(null); }}
              existingChartId={chartEditId}
              onSaved={async (chart) => {
                if (!dashboard || !editTarget) return;
                setDashboard({
                  ...dashboard,
                  widgets: widgets.map((w) => w.id === editTarget.id ? { ...w, title: chart.name } : w),
                });
                const preview = await dashboardService.previewWidget("chart", JSON.stringify({ chartId: chart.id })).catch(() => undefined);
                if (preview !== undefined) setData((prev) => ({ ...prev, [editTarget.id]: preview }));
                setEditTarget(null);
              }}
            />
            <MaterialisedViewBuilderModal
              open={!!materialisedViewEditId}
              onOpenChange={(o) => { if (!o) setEditTarget(null); }}
              existingViewId={materialisedViewEditId}
              onSaved={async (view) => {
                if (!dashboard || !editTarget || !view.id) return;
                setDashboard({
                  ...dashboard,
                  widgets: widgets.map((w) => w.id === editTarget.id ? { ...w, title: view.name } : w),
                });
                const preview = await dashboardService.previewWidget("materialised_view", JSON.stringify({ viewId: view.id })).catch(() => undefined);
                if (preview !== undefined) setData((prev) => ({ ...prev, [editTarget.id]: preview }));
                setEditTarget(null);
              }}
            />
          </>
        );
      })()}

      <NewDashboardModal
        open={newDashboardOpen}
        onOpenChange={setNewDashboardOpen}
        onSubmit={handleCreateDashboard}
      />
    </PageTemplate>
  );
}

function parseLayout(layoutJson: string | null): LayoutItem[] {
  if (!layoutJson) return [];
  try {
    const parsed = JSON.parse(layoutJson);
    if (Array.isArray(parsed)) return parsed as LayoutItem[];
  } catch {
    // ignore
  }
  return [];
}
